import type { ReactNode } from 'react';
import type { PartyStatus } from 'components/status-tag';
import type { WellSectionStatus } from 'pages/afe-catalog/structuring/types';
import { format0 } from 'utils/number';
import type {
  AfeTreeInput,
  AnchorSource,
  DivisionOrderTreeInput,
  EntityAddress,
  EntityCard,
  OverviewAnchor,
  OverviewTreeInput,
  RoyaltyCheckTreeInput,
  SharedWith,
  StructuredPartyListItem,
  StructuringTreeNode,
  SuggestionSummary,
  TreeWellsSection,
  WellDrillIn,
} from './types';

/**
 * Lists a card's addresses, falling back to its primary and secondary address
 * when it carries no address list.
 *
 * @param {EntityCard | null | undefined} card - The entity card to read.
 * @return {Array<EntityAddress & { primary?: boolean }>} The card's addresses, primary first.
 */
export const cardAddresses = (
  card: EntityCard | null | undefined
): Array<EntityAddress & { primary?: boolean }> => {
  if (!card) return [];
  if (card.addresses && card.addresses.length > 0) return card.addresses;
  if (card.primaryAddress) {
    return [
      { ...card.primaryAddress, primary: true },
      ...(card.secondaryAddress
        ? [
            { ...card.secondaryAddress, primary: false } as EntityAddress & {
              primary?: boolean;
            },
          ]
        : []),
    ];
  }
  return [];
};

const ANCHOR_SOURCE_LABELS: Record<
  AnchorSource,
  { title: string; inline: string }
> = {
  staged: { title: 'Staged Entity from Document', inline: 'staged entity' },
  deduplicated: {
    title: 'Deduplicated Result',
    inline: 'Deduplicated Result',
  },
};

/**
 * Labels where a party's current anchor came from.
 *
 * @param {AnchorSource | null | undefined} source - The anchor source; missing means staged.
 * @param {'title' | 'inline'} form - A heading's label, or the one that reads mid-sentence.
 * @return {string} The display label for the source.
 */
export const anchorSourceLabel = (
  source: AnchorSource | null | undefined,
  form: 'title' | 'inline' = 'title'
) => ANCHOR_SOURCE_LABELS[source ?? 'staged'][form];

type AnchorFields = Pick<
  StructuredPartyListItem,
  'currentAnchor' | 'stagedEntity' | 'name'
>;

/**
 * Tells whether the party's current anchor is a deduplicated result.
 *
 * @param {Pick<AnchorFields, 'currentAnchor'>} party - The party to check.
 * @return {boolean} True when the anchor came from deduplication.
 */
export const isDeduplicatedResult = (
  party: Pick<AnchorFields, 'currentAnchor'>
) => party.currentAnchor?.source === 'deduplicated';

/**
 * The card for the party's current anchor. While the anchor is still the staged
 * entity, the staged card is used: it carries the document-scoped addresses.
 *
 * @param {Pick<AnchorFields, 'currentAnchor' | 'stagedEntity'>} party - The party to read.
 * @return {EntityCard | null} The anchor's card, or null when there is none.
 */
export const anchorCard = (
  party: Pick<AnchorFields, 'currentAnchor' | 'stagedEntity'>
): EntityCard | null => {
  if (isDeduplicatedResult(party)) return party.currentAnchor;
  return party.stagedEntity ?? party.currentAnchor;
};

/**
 * The name of the party's current anchor. The staged entity goes by the
 * party's name as found on the document.
 *
 * @param {AnchorFields} party - The party to read.
 * @return {string} The anchor's display name.
 */
export const anchorName = (party: AnchorFields): string =>
  (isDeduplicatedResult(party) && party.currentAnchor?.name) || party.name;

export const SUGGESTION_PREFIX = 'Suggestion';
export const FIRST_SUGGESTION_LABEL = `${SUGGESTION_PREFIX} 1`;
export const USER_SELECTED_LABEL = 'User Selected';
export const USER_CREATED_LABEL = 'User Created';

/**
 * The label every surface shows for a suggestion: user rows by origin, scored
 * rows numbered among themselves in the order the server ranks them. The
 * no-match sentinel (no entity) gets no label.
 *
 * @param {SuggestionSummary[]} suggestions - The suggestions in server rank order.
 * @return {Map<number, string>} Labels keyed by suggestion id.
 */
export const suggestionLabels = (
  suggestions: SuggestionSummary[]
): Map<number, string> => {
  const labels = new Map<number, string>();
  let scored = 0;
  suggestions.forEach((suggestion) => {
    if (!suggestion.displayEntity && !suggestion.suggestedEntity) return;
    if (suggestion.userCreated) {
      labels.set(suggestion.id, USER_CREATED_LABEL);
    } else if (suggestion.userSelected) {
      labels.set(suggestion.id, USER_SELECTED_LABEL);
    } else {
      scored += 1;
      labels.set(suggestion.id, `${SUGGESTION_PREFIX} ${scored}`);
    }
  });
  return labels;
};

/**
 * Formats a count with the singular or plural noun to match.
 *
 * @param {number} count - The count to show.
 * @param {string} one - The noun for a count of one.
 * @param {string} many - The noun for any other count.
 * @return {string} The formatted count and noun.
 */
const plural = (count: number, one: string, many: string) =>
  `${format0(count)} ${count === 1 ? one : many}`;

/**
 * Discloses the other parties an action on a shared anchor also updates.
 * Resolving a shared anchor resolves every party that points at it, on every
 * document, so each action discloses that before it runs.
 *
 * @param {SharedWith | null | undefined} sharedWith - Who else shares the anchor.
 * @param {string} subject - The sentence's subject, e.g. the action's name.
 * @return {string | null} The disclosure, or null when nothing else is affected.
 */
export const sharedAnchorMessage = (
  sharedWith: SharedWith | null | undefined,
  subject = 'This'
): string | null => {
  if (!sharedWith) return null;
  const {
    thisDocumentPartyCount,
    otherDocumentPartyCount,
    otherDocumentCount,
  } = sharedWith;
  const parts = [
    thisDocumentPartyCount > 0 &&
      `${plural(
        thisDocumentPartyCount,
        'other party',
        'other parties'
      )} on this document`,
    otherDocumentPartyCount > 0 &&
      `${plural(otherDocumentPartyCount, 'party', 'parties')} on ${plural(
        otherDocumentCount,
        'other document',
        'other documents'
      )}`,
  ].filter(Boolean);
  if (parts.length === 0) return null;
  return `${subject} also updates ${parts.join(' and ')}.`;
};

/**
 * Scroll-spy map: each Structured Overview anchored section (in scroll order) →
 * the left-nav node it highlights while in view. Sections a document doesn't
 * render are absent from the page and filtered out before observing.
 */
export const OVERVIEW_ANCHORS = [
  { anchor: 'so-parties', node: 'tree-parties' },
  { anchor: 'so-doc-addresses', node: 'tree-doc-addresses' },
  { anchor: 'so-afe-identification', node: 'tree-afe-identification' },
  { anchor: 'so-afe-wells', node: 'tree-afe-wells' },
  { anchor: 'so-afe-costs', node: 'tree-afe-costs' },
  { anchor: 'so-afe-related-items', node: 'tree-afe-related-items' },
  { anchor: 'so-do-overview', node: 'tree-do-overview' },
  { anchor: 'so-do-wells', node: 'tree-do-wells' },
  { anchor: 'so-do-interests', node: 'tree-do-interests' },
  { anchor: 'so-do-related-items', node: 'tree-do-related-items' },
  { anchor: 'so-rc-overview', node: 'tree-rc-overview' },
  { anchor: 'so-rc-wells', node: 'tree-rc-wells' },
  { anchor: 'so-rc-line-items', node: 'tree-rc-line-items' },
  { anchor: 'so-rc-financial-summary', node: 'tree-rc-financial-summary' },
  { anchor: 'so-rc-related-items', node: 'tree-rc-related-items' },
  { anchor: 'so-doc-vendor-information', node: 'tree-doc-vendor-information' },
  { anchor: 'so-doc-related-items', node: 'tree-doc-related-items' },
  { anchor: 'so-audit-log', node: 'tree-audit-log' },
] as const;

/**
 * Folds the section status vocabularies (which include `user_added`, `edited`…)
 * onto the four states the tree's status icons know.
 *
 * @param {WellSectionStatus | PartyStatus} status - A section's status.
 * @return {PartyStatus} The tree status icon's state.
 */
export const toNodeStatus = (
  status: WellSectionStatus | PartyStatus
): PartyStatus => {
  if (status === 'complete') return 'complete';
  if (status === 'removed') return 'removed';
  if (status === 'not_started') return 'not_started';
  return 'in_progress';
};

/**
 * The tree label for a party: its position, name and role.
 *
 * @param {StructuredPartyListItem} party - The party.
 * @param {number} index - The party's position in the list.
 * @return {string} The label text.
 */
const partyText = (party: StructuredPartyListItem, index: number) =>
  [`Party ${index + 1}`, party.name || '—', party.role]
    .filter(Boolean)
    .join(' · ');

const MATCHING_IN_PROGRESS = 'Matching in progress';

/**
 * What runs on a party, for its spinner: an address parse when it runs alone,
 * else matching.
 *
 * @param {StructuredPartyListItem} party - The party.
 * @return {string | undefined} The spinner's description; none while nothing runs.
 */
const partyRunning = (party: StructuredPartyListItem) => {
  if (!party.agentRunInProgress) return undefined;
  return party.addressParseInProgress
    ? 'Parsing addresses'
    : MATCHING_IN_PROGRESS;
};

/**
 * The Well Information node, with one child per well.
 *
 * @param {string} prefix - The document type's node id prefix.
 * @param {OverviewAnchor} anchor - The section the node scrolls to.
 * @param {TreeWellsSection} section - The wells section's status and wells.
 * @param {Function} goToSection - Scrolls to an overview section.
 * @return {StructuringTreeNode} The wells node.
 */
const wellsNode = (
  prefix: string,
  anchor: OverviewAnchor,
  section: TreeWellsSection,
  goToSection: (anchor: OverviewAnchor) => void
): StructuringTreeNode => ({
  id: `tree-${prefix}-wells`,
  text: 'Well Information',
  status: toNodeStatus(section.status),
  running: section.running ? MATCHING_IN_PROGRESS : undefined,
  onSelect: () => goToSection(anchor),
  children: section.wells.map((well, index) => ({
    id: `${prefix}-well-${well.id}`,
    text: well.stagedWell.wellName || '—',
    status: toNodeStatus(well.status),
    removed: well.status === 'removed',
    running: well.agentRunInProgress ? MATCHING_IN_PROGRESS : undefined,
    onSelect: () => section.onSelectWell(index),
  })),
});

/**
 * A Related Items node that scrolls to its section.
 *
 * @param {string} id - The node id.
 * @param {OverviewAnchor} anchor - The section the node scrolls to.
 * @param {Function} goToSection - Scrolls to an overview section.
 * @return {StructuringTreeNode} The related items node.
 */
const relatedItemsNode = (
  id: string,
  anchor: OverviewAnchor,
  goToSection: (anchor: OverviewAnchor) => void
): StructuringTreeNode => ({
  id,
  text: 'Related Items',
  onSelect: () => goToSection(anchor),
});

/**
 * The AFE document's section nodes.
 *
 * @param {AfeTreeInput} afe - The AFE sections' state.
 * @param {Function} goToSection - Scrolls to an overview section.
 * @return {StructuringTreeNode[]} The AFE nodes.
 */
const afeNodes = (
  afe: AfeTreeInput,
  goToSection: (anchor: OverviewAnchor) => void
): StructuringTreeNode[] => [
  {
    id: 'tree-afe-identification',
    text: 'AFE Identification',
    status: toNodeStatus(afe.identificationStatus),
    onSelect: () => goToSection('so-afe-identification'),
  },
  wellsNode('afe', 'so-afe-wells', afe.wellsSection, goToSection),
  {
    id: 'tree-afe-costs',
    text: 'Cost Information',
    status: afe.costStatus,
    onSelect: () => goToSection('so-afe-costs'),
    children: afe.costWells.map((well) => ({
      id: `afe-cost-well-${well.afeHeaderId}`,
      text: well.wellName || '—',
      status: well.removed ? 'removed' : toNodeStatus(well.status),
      removed: well.removed,
      onSelect: () => afe.onSelectCostWell(well.afeHeaderId),
    })),
  },
  relatedItemsNode(
    'tree-afe-related-items',
    'so-afe-related-items',
    goToSection
  ),
];

/**
 * The division order document's section nodes.
 *
 * @param {DivisionOrderTreeInput} divisionOrder - The division order sections' state.
 * @param {Function} goToSection - Scrolls to an overview section.
 * @return {StructuringTreeNode[]} The division order nodes.
 */
const divisionOrderNodes = (
  divisionOrder: DivisionOrderTreeInput,
  goToSection: (anchor: OverviewAnchor) => void
): StructuringTreeNode[] => [
  {
    id: 'tree-do-overview',
    text: 'Division Order Overview',
    status: toNodeStatus(divisionOrder.overviewStatus),
    onSelect: () => goToSection('so-do-overview'),
  },
  wellsNode('do', 'so-do-wells', divisionOrder.wellsSection, goToSection),
  {
    id: 'tree-do-interests',
    text: 'Interest Review',
    status: divisionOrder.interestStatus,
    onSelect: () => goToSection('so-do-interests'),
  },
  relatedItemsNode('tree-do-related-items', 'so-do-related-items', goToSection),
];

/**
 * The royalty check document's section nodes.
 *
 * @param {RoyaltyCheckTreeInput} royaltyCheck - The royalty check sections' state.
 * @param {Function} goToSection - Scrolls to an overview section.
 * @return {StructuringTreeNode[]} The royalty check nodes.
 */
const royaltyCheckNodes = (
  royaltyCheck: RoyaltyCheckTreeInput,
  goToSection: (anchor: OverviewAnchor) => void
): StructuringTreeNode[] => [
  {
    id: 'tree-rc-overview',
    text: 'Royalty Check Overview',
    status: toNodeStatus(royaltyCheck.overviewStatus),
    onSelect: () => goToSection('so-rc-overview'),
  },
  wellsNode('rc', 'so-rc-wells', royaltyCheck.wellsSection, goToSection),
  {
    id: 'tree-rc-line-items',
    text: 'Line Item Review',
    status: toNodeStatus(royaltyCheck.lineItemStatus),
    onSelect: () => goToSection('so-rc-line-items'),
    children: royaltyCheck.lineItemGroups.map((group, index) => ({
      id: `rc-line-item-${group.key}`,
      text: group.wellName ?? 'Unassigned',
      status: toNodeStatus(group.status),
      onSelect: () => royaltyCheck.onSelectLineItemGroup(index),
    })),
  },
  ...(royaltyCheck.financialSummaryStatus
    ? [
        {
          id: 'tree-rc-financial-summary',
          text: 'Check Financial Summary',
          status: toNodeStatus(royaltyCheck.financialSummaryStatus),
          onSelect: () => goToSection('so-rc-financial-summary'),
        },
      ]
    : []),
  relatedItemsNode('tree-rc-related-items', 'so-rc-related-items', goToSection),
];

/**
 * The section nodes for a document type without its own structuring.
 *
 * @param {Function} goToSection - Scrolls to an overview section.
 * @return {StructuringTreeNode[]} The generic nodes.
 */
const genericNodes = (
  goToSection: (anchor: OverviewAnchor) => void
): StructuringTreeNode[] => [
  {
    id: 'tree-doc-vendor-information',
    text: 'Vendor Information',
    onSelect: () => goToSection('so-doc-vendor-information'),
  },
  relatedItemsNode(
    'tree-doc-related-items',
    'so-doc-related-items',
    goToSection
  ),
];

/**
 * The children of the Structured Overview node: parties and addresses on every
 * document, then the document type's own sections, then the activity log.
 *
 * @param {OverviewTreeInput} input - The document's parties and section states.
 * @return {StructuringTreeNode[]} The overview's child nodes.
 */
export const buildOverviewTree = ({
  parties,
  partiesStatus,
  addressesStatus,
  afe,
  divisionOrder,
  royaltyCheck,
  goToSection,
  onSelectParty,
}: OverviewTreeInput): StructuringTreeNode[] => {
  const doctypeNodes = afe
    ? afeNodes(afe, goToSection)
    : divisionOrder
      ? divisionOrderNodes(divisionOrder, goToSection)
      : royaltyCheck
        ? royaltyCheckNodes(royaltyCheck, goToSection)
        : genericNodes(goToSection);
  return [
    {
      id: 'tree-parties',
      text: 'Parties',
      status: partiesStatus,
      onSelect: () => goToSection('so-parties'),
      children: parties.map((party, index) => ({
        id: `party-${party.kind}-${party.id}`,
        text: partyText(party, index),
        status: party.status,
        removed: party.status === 'removed',
        running: partyRunning(party),
        onSelect: () => onSelectParty(index),
      })),
    },
    {
      id: 'tree-doc-addresses',
      text: 'Non-Party Addresses',
      status: addressesStatus,
      onSelect: () => goToSection('so-doc-addresses'),
    },
    ...doctypeNodes,
    {
      id: 'tree-audit-log',
      text: 'Activity Log',
      onSelect: () => goToSection('so-audit-log'),
    },
  ];
};

interface PagedDrillInOptions {
  nodeId: string;
  index: number;
  count: number;
  // null leaves the drill-in.
  setIndex: (index: number | null) => void;
  renderMatch: () => ReactNode;
}

/**
 * Builds a drill-in that pages through a list (wells, line-item groups) by index.
 *
 * @param {PagedDrillInOptions} options - The node, position, count, index setter and match renderer.
 * @return {WellDrillIn} The drill-in with its paging callbacks.
 */
export const pagedDrillIn = ({
  nodeId,
  index,
  count,
  setIndex,
  renderMatch,
}: PagedDrillInOptions): WellDrillIn => ({
  nodeId,
  index,
  count,
  atFirst: index === 0,
  atLast: index === count - 1,
  onPrev: () => setIndex(Math.max(0, index - 1)),
  onNext: () => setIndex(Math.min(count - 1, index + 1)),
  onBack: () => setIndex(null),
  renderMatch,
});
